/**
 * Forgot password — step 1 (email + request code). Same API as the mobile app.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { requestPasswordResetCode } from "../../../redux/api/auth";
import { FORGOT_PASSWORD_EMAIL_SESSION_KEY } from "../../../utils/forgotPasswordSession";

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleContinue = async () => {
    const trimmed = email.toLowerCase().trim();
    if (!trimmed.includes("@")) {
      setErrorMessage("Please enter a valid email address.");
      setErrorOpen(true);
      return;
    }
    setLoading(true);
    try {
      const { ok, response } = await requestPasswordResetCode(trimmed);
      if (ok) {
        try {
          sessionStorage.setItem(FORGOT_PASSWORD_EMAIL_SESSION_KEY, trimmed);
        } catch (e) {
          // non-fatal
        }
        navigate("/authentication/forgot-password/confirm");
        return;
      }
      setErrorMessage(
        response?.message || "We could not send a verification code. Please try again."
      );
      setErrorOpen(true);
    } catch (e) {
      setErrorMessage("Something went wrong. Please try again.");
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const disabled = !email.trim() || loading;

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translate(-50%, -60%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={require("../../../assets/images/logos/mini_logo.png")}
            style={{ height: 70, width: 70, borderRadius: 10 }}
            alt=""
          />
        </div>
        <MDBox mb={2} />
        <MDBox pt={4} pb={3} px={3}>
          <MDBox mb={1}>
            <MDTypography variant="caption" color="text">
              Step 1 of 2
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="h5" fontWeight="medium">
              Recover password
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text">
              Enter the email for your Microlab account. We will send you a verification code.
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              type="email"
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </MDBox>
          <MDBox mt={3} mb={1} display="flex" gap={1}>
            <MDButton
              component={Link}
              to="/authentication/sign-in"
              variant="outlined"
              color="dark"
              fullWidth
            >
              Back
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={disabled}
              onClick={handleContinue}
            >
              {loading ? "Sending…" : "Continue"}
            </MDButton>
          </MDBox>
          <MDBox mt={2} textAlign="center">
            <MDTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="info"
              fontWeight="medium"
              sx={{ textDecoration: "none" }}
            >
              Back to sign in
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
      <MDSnackbar
        color="error"
        icon="warning"
        title={errorMessage}
        dateTime=""
        content=""
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        close={() => setErrorOpen(false)}
        bgWhite
      />
    </BasicLayout>
  );
}

export default ForgotPassword;
